import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Tag } from '../../../core/types/tag';
import { TagService } from '../../../core/services/tag.service';
import { SearchOptions } from '@app/core/types/search-options';
import { Page } from '@app/core/types/page';
import { Subject } from 'rxjs';
import { finalize, map, takeUntil } from 'rxjs/operators';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { TagSettingsCardComponent } from '../tag-settings-card/tag-settings-card.component';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '@app/core/services/notification.service';

@Component({
    selector: 'itfg-tags-dropdown',
    templateUrl: './tags-dropdown.component.html',
    styleUrls: ['./tags-dropdown.component.scss'],
    standalone: false
})
export class TagDropdownComponent implements OnInit, OnDestroy, OnChanges {
  @Input() entityId: number; // Either clientId or creditId
  @Input() appliedTags: Tag[] = [];
  @Input() tagLimit: number = 15;
  @Input() menuTrigger: MatMenuTrigger;
  @Output() onTagSelected = new EventEmitter<{
    id: number;
    tagId: number;
    tagValue: Tag;
  }>();

  @ViewChild('searchInput') searchInput: ElementRef;

  public tagsList: Tag[] = [];
  public filteredTags: Tag[] = [];
  public searchText: string = '';
  public isLoadingTags: boolean = false;

  private _unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private tagService: TagService,
    private dialog: MatDialog,
    private notifications: NotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadTags();

    this.tagService.onTagPreferencesChanged
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => {
        this.loadTags();
      });

    this.tagService.onTagOrderChanged
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => {
        this.loadTags();
      });

    this.tagService.onTagAddedOrDeleted
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => {
        this.loadTags();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entityId || changes.appliedTags) {
      this.loadTags();
    }
  }

  focusSearchInput(): void {
    if (this.searchInput?.nativeElement && !this.isLoadingTags) {
      this.searchInput.nativeElement.focus();
    }
  }

  loadTags(): void {
    this.isLoadingTags = true;

    this.tagService
      .getTagList$(new SearchOptions({ pageSize: 6000 }).getDTO())
      .pipe(
        map((page: Page<Tag>) => page.content),
        takeUntil(this._unsubscribe),
        finalize(() => (this.isLoadingTags = false))
      )
      .subscribe({
        next: (tagList: Tag[]) => {
          const tagsWithPreferences =
            this.tagService.applyTagsPreferences(tagList);

          const visibleTags = tagsWithPreferences.filter(
            (tag: Tag) => !!tag.visible
          );

          this.tagsList = visibleTags.filter(
            (tag) =>
              !this.appliedTags?.some(
                (appliedTag: Tag) => appliedTag.id === tag.id
              )
          );

          this.filterTags();
          this.cdr.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          this.notifications.showLocalizedErrorMessage({
            notificationText: 'tags.fetchTagsFailMessage',
          });
          console.error('Error loading tags:', error);
        },
      });
  }

  filterTags(): void {
    if (!this.searchText || this.searchText.trim() === '') {
      this.filteredTags = [...this.tagsList];
    } else {
      const searchLower = this.searchText.toLowerCase();
      this.filteredTags = this.tagsList.filter(
        (tag: Tag) =>
          tag.name.toLowerCase().includes(searchLower) ||
          (tag.description &&
            tag.description.toLowerCase().includes(searchLower))
      );
    }
  }

  onSearchChange(): void {
    this.filterTags();
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.searchText = '';
    this.filterTags();
    if (this.searchInput) {
      this.searchInput.nativeElement.focus();
    }
  }

  addTag(tag: Tag, event: Event): void {
    event.stopPropagation();

    this.onTagSelected.emit({
      id: this.entityId,
      tagId: tag.id,
      tagValue: tag,
    });

    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
      this.searchText = '';
      this.filterTags();
    }
  }

  openTagSettings(event: Event): void {
    event.stopPropagation();

    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
    }

    const dialogRef = this.dialog.open(TagSettingsCardComponent, {
      width: '650px',
      autoFocus: false,
    });
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
